// Spatial Partitioning System using Quadtree
// Foundation for scalability - O(log n) neighbor lookups instead of O(n)
// Inspired by all major MMOs for handling hundreds of entities efficiently

import * as THREE from 'three';

export interface SpatialEntity {
  id: string;
  position: THREE.Vector3;
  radius?: number; // For collision/range queries
}

interface QuadTreeNode {
  bounds: { x: number; z: number; width: number; height: number };
  entities: SpatialEntity[];
  children: QuadTreeNode[] | null;
  depth: number;
}

export class Quadtree {
  private root: QuadTreeNode;
  private readonly maxEntities: number;
  private readonly maxDepth: number;
  private entityToNode: Map<string, QuadTreeNode> = new Map();

  // Reusable arrays for queries (avoid allocations)
  private readonly _queryResult: SpatialEntity[] = [];
  private readonly _nodesToCheck: QuadTreeNode[] = [];

  constructor(
    bounds: { x: number; z: number; width: number; height: number },
    maxEntities: number = 8,
    maxDepth: number = 6
  ) {
    this.maxEntities = maxEntities;
    this.maxDepth = maxDepth;
    this.root = this.createNode(bounds, 0);
  }

  private createNode(
    bounds: { x: number; z: number; width: number; height: number },
    depth: number
  ): QuadTreeNode {
    return {
      bounds,
      entities: [],
      children: null,
      depth,
    };
  }

  public insert(entity: SpatialEntity): void {
    this.insertIntoNode(this.root, entity);
  }

  private insertIntoNode(node: QuadTreeNode, entity: SpatialEntity): void {
    // Check if entity is within bounds
    if (!this.containsPoint(node.bounds, entity.position)) {
      return;
    }

    // If node has children, insert into appropriate child
    if (node.children) {
      for (const child of node.children) {
        if (this.containsPoint(child.bounds, entity.position)) {
          this.insertIntoNode(child, entity);
          return;
        }
      }
    }

    // Add to this node
    node.entities.push(entity);
    this.entityToNode.set(entity.id, node);

    // Split if necessary
    if (node.entities.length > this.maxEntities && node.depth < this.maxDepth && !node.children) {
      this.split(node);
    }
  }

  private split(node: QuadTreeNode): void {
    const { x, z, width, height } = node.bounds;
    const halfWidth = width / 2;
    const halfHeight = height / 2;

    node.children = [
      this.createNode({ x, z, width: halfWidth, height: halfHeight }, node.depth + 1),
      this.createNode({ x: x + halfWidth, z, width: halfWidth, height: halfHeight }, node.depth + 1),
      this.createNode({ x, z: z + halfHeight, width: halfWidth, height: halfHeight }, node.depth + 1),
      this.createNode({ x: x + halfWidth, z: z + halfHeight, width: halfWidth, height: halfHeight }, node.depth + 1),
    ];

    // Redistribute entities
    const entities = node.entities;
    node.entities = [];

    for (const entity of entities) {
      this.entityToNode.delete(entity.id);
      for (const child of node.children) {
        if (this.containsPoint(child.bounds, entity.position)) {
          this.insertIntoNode(child, entity);
          break;
        }
      }
    }
  }

  public remove(entityId: string): void {
    const node = this.entityToNode.get(entityId);
    if (!node) return;

    const index = node.entities.findIndex(e => e.id === entityId);
    if (index !== -1) {
      node.entities.splice(index, 1);
    }
    this.entityToNode.delete(entityId);
  }

  public update(entity: SpatialEntity): void {
    const currentNode = this.entityToNode.get(entity.id);
    if (!currentNode) {
      this.insert(entity);
      return;
    }

    // Check if still in same node bounds
    if (this.containsPoint(currentNode.bounds, entity.position)) {
      // Update position in place (entity reference already updated)
      return;
    }

    // Entity moved to different region
    this.remove(entity.id);
    this.insert(entity);
  }

  // Query entities within a radius
  public queryRadius(center: THREE.Vector3, radius: number): SpatialEntity[] {
    this._queryResult.length = 0;
    const radiusSq = radius * radius;

    this._nodesToCheck.length = 0;
    this._nodesToCheck.push(this.root);

    while (this._nodesToCheck.length > 0) {
      const node = this._nodesToCheck.pop()!;

      // Check if query circle intersects node bounds
      if (!this.circleIntersectsBounds(center, radius, node.bounds)) {
        continue;
      }

      // Check entities in this node
      for (const entity of node.entities) {
        const dx = entity.position.x - center.x;
        const dz = entity.position.z - center.z;
        if (dx * dx + dz * dz <= radiusSq) {
          this._queryResult.push(entity);
        }
      }

      // Add children to check
      if (node.children) {
        for (const child of node.children) {
          this._nodesToCheck.push(child);
        }
      }
    }

    return this._queryResult;
  }

  // Query entities within a rectangle (for frustum culling)
  public queryRect(
    minX: number,
    minZ: number,
    maxX: number,
    maxZ: number
  ): SpatialEntity[] {
    this._queryResult.length = 0;

    this._nodesToCheck.length = 0;
    this._nodesToCheck.push(this.root);

    while (this._nodesToCheck.length > 0) {
      const node = this._nodesToCheck.pop()!;

      // Check if query rect intersects node bounds
      if (!this.rectIntersectsBounds(minX, minZ, maxX, maxZ, node.bounds)) {
        continue;
      }

      // Check entities in this node
      for (const entity of node.entities) {
        const x = entity.position.x;
        const z = entity.position.z;
        if (x >= minX && x <= maxX && z >= minZ && z <= maxZ) {
          this._queryResult.push(entity);
        }
      }

      // Add children to check
      if (node.children) {
        for (const child of node.children) {
          this._nodesToCheck.push(child);
        }
      }
    }

    return this._queryResult;
  }

  // Get K nearest entities to a point
  public queryKNearest(center: THREE.Vector3, k: number, maxRadius: number = Infinity): SpatialEntity[] {
    const candidates = maxRadius < Infinity
      ? this.queryRadius(center, maxRadius)
      : this.getAllEntities();

    // Sort by distance
    candidates.sort((a, b) => {
      const distA = (a.position.x - center.x) ** 2 + (a.position.z - center.z) ** 2;
      const distB = (b.position.x - center.x) ** 2 + (b.position.z - center.z) ** 2;
      return distA - distB;
    });

    return candidates.slice(0, k);
  }

  public getAllEntities(): SpatialEntity[] {
    this._queryResult.length = 0;
    this.collectEntities(this.root);
    return [...this._queryResult];
  }

  private collectEntities(node: QuadTreeNode): void {
    for (const entity of node.entities) {
      this._queryResult.push(entity);
    }
    if (node.children) {
      for (const child of node.children) {
        this.collectEntities(child);
      }
    }
  }

  private containsPoint(
    bounds: { x: number; z: number; width: number; height: number },
    point: THREE.Vector3
  ): boolean {
    return (
      point.x >= bounds.x &&
      point.x < bounds.x + bounds.width &&
      point.z >= bounds.z &&
      point.z < bounds.z + bounds.height
    );
  }

  private circleIntersectsBounds(
    center: THREE.Vector3,
    radius: number,
    bounds: { x: number; z: number; width: number; height: number }
  ): boolean {
    // Find closest point on rectangle to circle center
    const closestX = Math.max(bounds.x, Math.min(center.x, bounds.x + bounds.width));
    const closestZ = Math.max(bounds.z, Math.min(center.z, bounds.z + bounds.height));

    const dx = center.x - closestX;
    const dz = center.z - closestZ;

    return dx * dx + dz * dz <= radius * radius;
  }

  private rectIntersectsBounds(
    minX: number,
    minZ: number,
    maxX: number,
    maxZ: number,
    bounds: { x: number; z: number; width: number; height: number }
  ): boolean {
    return !(
      maxX < bounds.x ||
      minX > bounds.x + bounds.width ||
      maxZ < bounds.z ||
      minZ > bounds.z + bounds.height
    );
  }

  public clear(): void {
    this.root = this.createNode(this.root.bounds, 0);
    this.entityToNode.clear();
  }

  // Debug visualization
  public getDebugLines(): THREE.LineSegments {
    const positions: number[] = [];
    this.collectDebugLines(this.root, positions);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

    const material = new THREE.LineBasicMaterial({ color: 0x00ff00, opacity: 0.3, transparent: true });
    return new THREE.LineSegments(geometry, material);
  }

  private collectDebugLines(node: QuadTreeNode, positions: number[]): void {
    const { x, z, width, height } = node.bounds;
    const y = 0.1;

    // Draw rectangle
    positions.push(x, y, z, x + width, y, z);
    positions.push(x + width, y, z, x + width, y, z + height);
    positions.push(x + width, y, z + height, x, y, z + height);
    positions.push(x, y, z + height, x, y, z);

    if (node.children) {
      for (const child of node.children) {
        this.collectDebugLines(child, positions);
      }
    }
  }
}

// Spatial Hash Grid - Alternative for uniform entity distribution
// Better for scenarios where entities are evenly spread
export class SpatialHashGrid {
  private cellSize: number;
  private grid: Map<string, Set<SpatialEntity>> = new Map();
  private entityToCell: Map<string, string> = new Map();

  constructor(cellSize: number = 10) {
    this.cellSize = cellSize;
  }

  private getCellKey(x: number, z: number): string {
    const cellX = Math.floor(x / this.cellSize);
    const cellZ = Math.floor(z / this.cellSize);
    return `${cellX},${cellZ}`;
  }

  public insert(entity: SpatialEntity): void {
    const key = this.getCellKey(entity.position.x, entity.position.z);

    if (!this.grid.has(key)) {
      this.grid.set(key, new Set());
    }
    this.grid.get(key)!.add(entity);
    this.entityToCell.set(entity.id, key);
  }

  public remove(entityId: string): void {
    const key = this.entityToCell.get(entityId);
    if (!key) return;

    const cell = this.grid.get(key);
    if (cell) {
      for (const entity of cell) {
        if (entity.id === entityId) {
          cell.delete(entity);
          break;
        }
      }
    }
    this.entityToCell.delete(entityId);
  }

  public update(entity: SpatialEntity): void {
    const oldKey = this.entityToCell.get(entity.id);
    const newKey = this.getCellKey(entity.position.x, entity.position.z);

    if (oldKey === newKey) return;

    this.remove(entity.id);
    this.insert(entity);
  }

  public queryRadius(center: THREE.Vector3, radius: number): SpatialEntity[] {
    const results: SpatialEntity[] = [];
    const radiusSq = radius * radius;

    // Calculate cell range to check
    const minCellX = Math.floor((center.x - radius) / this.cellSize);
    const maxCellX = Math.floor((center.x + radius) / this.cellSize);
    const minCellZ = Math.floor((center.z - radius) / this.cellSize);
    const maxCellZ = Math.floor((center.z + radius) / this.cellSize);

    for (let cellX = minCellX; cellX <= maxCellX; cellX++) {
      for (let cellZ = minCellZ; cellZ <= maxCellZ; cellZ++) {
        const key = `${cellX},${cellZ}`;
        const cell = this.grid.get(key);
        if (!cell) continue;

        for (const entity of cell) {
          const dx = entity.position.x - center.x;
          const dz = entity.position.z - center.z;
          if (dx * dx + dz * dz <= radiusSq) {
            results.push(entity);
          }
        }
      }
    }

    return results;
  }

  public clear(): void {
    this.grid.clear();
    this.entityToCell.clear();
  }
}
